import {gsap} from "gsap/gsap-core";
import {Types2D} from "@dp-instant/interfaces";
import {Mediator} from "@dp-instant/2d";
import {GameModel} from "@game/coregame/model/GameModel";
import {TypesGame} from "@game/TypesGame";
import {inject, injectable} from "inversify";
import {GameEvents} from "../GameEvents";
import {FieldView} from "../view/FieldView";
import {DisplaySystem} from "@game/coregame/ecs/systems/DisplaySystem";
import svgPathBbox from "svg-path-bbox";
import Root from "@game/coregame/view/Root";
import {GameState} from "@game/coregame/model/GameState";
import {GameConstants} from "@game/coregame/constants";
import {SIOConstants} from "@game/constants";

type Bounds = [left: number, top: number, right: number, bottom: number];

@injectable()
export class FieldMediator extends Mediator {
    @inject(Types2D.rootView)
    protected root: Root;

    @inject(TypesGame.model)
    protected model: GameModel;
    protected view: FieldView;
    private _renderingSystem: DisplaySystem;

    initialize() {
        this._renderingSystem ||= new DisplaySystem(this.view);
        this.model.engine.addSystem(this._renderingSystem);
        this.view.alpha = 0;
        this.addListener(GameEvents.LEVEL_LOADED, () => {
            this.onStateChanged(this.model.state);
            gsap.to(this.view, {alpha: 1});
        });
        this.addListener(GameEvents.RESTART_LEVEL, () =>
            this.focusOn(this.model.currentContinent.stageLevel)
        );
        this.addListener(GameEvents.STATE_CHANGED, this.onStateChanged);
        this.addListener(GameEvents.RESIZE, this.onResize);
    }
    private onResize() {
        this.onStateChanged(this.model.state);
    }
    private onStateChanged(state: GameState) {
        switch (state) {
            case GameState.GAMEPLAY:
                this.focusOn(this.model.currentContinent.stageLevel);
                break;
            case GameState.LOBBY:
                this.focusOn(NaN, 0);
                break;
            case GameState.LOOSE:
            case GameState.WIN_STAGE:
            case GameState.WIN_CONTINENT:
                this.focusOn(NaN);
                break;
        }
    }

    private getMapBounds(stage?: number, statePosRadius = 150): Bounds {
        if (isNaN(stage as number)) {
            const localBounds = this.view.getLocalBounds();
            return [localBounds.left, localBounds.top, localBounds.right, localBounds.bottom];
        }
        const bounds: Bounds = [Infinity, Infinity, -Infinity, -Infinity];
        this.model.currentContinent.parsed.forEach((data, id) => {
            if (data.stage === stage) {
                if (isNaN(statePosRadius)) {
                    data.shapes?.forEach(shape => {
                        const shapeBounds: Bounds = svgPathBbox(shape);
                        bounds[0] = Math.min(bounds[0], shapeBounds[0]);
                        bounds[1] = Math.min(bounds[1], shapeBounds[1]);
                        bounds[2] = Math.max(bounds[2], shapeBounds[2]);
                        bounds[3] = Math.max(bounds[3], shapeBounds[3]);
                    });
                } else {
                    const [x, y] = data.statePos;
                    bounds[0] = Math.min(bounds[0], x - statePosRadius);
                    bounds[1] = Math.min(bounds[1], y - statePosRadius);
                    bounds[2] = Math.max(bounds[2], x + statePosRadius);
                    bounds[3] = Math.max(bounds[3], y + statePosRadius);
                }
            }
        });
        return bounds;
    }

    private focusOn(stage = 0, duration = 1.0) {
        const k = GameConstants.StartScreen.focusHeightFactor;
        const PROGRESS_BAR_HEIGHT = isNaN(stage) ? 0 : 100;
        const {width, height: screenHeight} = this.root.size;
        const height = isNaN(stage) ? screenHeight * k : screenHeight;
        let [left, top, right, bottom] = this.getMapBounds(stage);
        top -= PROGRESS_BAR_HEIGHT;
        bottom += SIOConstants.BANNER_HEIGHT;

        const picWidth = right - left,
            picHeight = bottom - top;

        const scaleX = width / picWidth,
            scaleY = height / picHeight,
            scale = Math.min(1, Math.min(scaleX, scaleY));

        const picWidth_s = picWidth * scale,
            picHeight_s = picHeight * scale,
            left_s = left * scale,
            top_s = top * scale;

        gsap.to(this.view.scale, {
            x: scale,
            y: scale,
            duration
        });

        gsap.to(this.view, {
            x: (width - picWidth_s) * 0.5 - left_s,
            y: (height - picHeight_s) * 0.5 - top_s,
            duration
        });
        this.model.currentContinent.buildings.forEach(building =>
            building.toggleActive(stage, duration)
        );
    }

    destroy() {
        this.model.engine.removeSystem(this._renderingSystem);
        super.destroy();
    }
}
